import { IconButton, Tooltip } from '@material-ui/core';
import { Clear } from '@material-ui/icons';
import cx from 'classnames';
import {
  icon_add_patient,
  icon_dentweb,
  icon_microphone_w,
  icon_project_calendar,
} from 'components/base/images';
import MuiButton from 'components/common/button/MuiButton';
import DatePickerController from 'components/common/input/DatePickerController';
import TextFieldController from 'components/common/input/TextFieldController';
import AppModal from 'components/common/modal/AppModal';
import ClinicModal from 'components/common/modal/ClinicModal';
import PlainModal from 'components/common/modal/PlainModal';
import DropdownController from 'components/common/select/DropdownController';
import MultiDropdownController from 'components/common/select/MultiDropdownController';
import SearchDropdownController from 'components/common/select/SearchDropdownController';
import { T } from 'components/common/text';
import CustomText from 'components/common/text/CustomText';
import PatientModalContainer from 'containers/patient/PatientModalContainer';
import { useCreateProjectInfo } from 'lib/hooks/project/useCreateProjectInfo';
import { IPatient } from 'lib/types/patientTypes';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import MicrophoneAlertModal from './MicrophoneAlertModal';

interface CreateProjectInfoProps {
  from: 'create' | 'detail' | 'patient';
}

const CreateProjectInfo = ({ from }: CreateProjectInfoProps) => {
  // SECTION: declare local store

  // SECTION: declare hooks
  const {
    control,
    watch,
    isEdit,
    isPatient,
    patientOptionList,
    patientName,
    managerOptionList,
    remakeOptionList,
    hasMoreProjectPatients,
    projectPatientsPending,
    isValidInfo,
    isOpenPatientModal,
    isOpenMicrophoneModal,
    isOpenDueDate,
    isRecording,
    isTranscriptionLoading,
    isNewPatient,
    recordingTime,
    loadedSource,
    onOptionClick,
    onPatientSearch,
    onPatientFetch,
    onCreateInfoClose,
    onCreateInfoSave,
    onDetailInfoSave,
    onCreatePatient,
    onClosePatientModal,
    onCloseMicrophoneModal,
    onClickDueDate,
    onOpenChangeDueDate,
    onMicrophoneClick,
    onConfirmMicrophoneModal,
    onProjectNameChange,
  } = useCreateProjectInfo(from);
  const { t } = useTranslation();

  return (
    <StyledCreateProjectInfo>
      {/* title box */}
      <div className="createProjectInfo__title_box">
        <CustomText fontSize={26} fontWeight={600} fontColor="#222">
          <T>PROJECT_INFORMATION_TITLE</T>
        </CustomText>
        <IconButton onClick={onCreateInfoClose}>
          <Clear />
        </IconButton>
      </div>
      {/* content box */}
      <div className="createProjectInfo__content_box">
        <div className="createProjectInfo__item_box patient">
          <div className="item_label">
            <CustomText fontSize={16} fontWeight={600} fontColor="#888">
              <T>GLOBAL_PATIENT</T>
            </CustomText>
            <CustomText fontSize={16} fontWeight={600} fontColor="#D40000">
              *
            </CustomText>
          </div>
          <div className="item_content">
            <SearchDropdownController
              name="patientCodeIdx"
              disabled={isEdit || isPatient}
              control={control}
              label={patientName || ''}
              maxLength={16}
              options={patientOptionList.map((option: IPatient) => {
                return {
                  ...{
                    value: option.patientCodeIdx,
                    label: `${option.lastName || ''}${option.firstName || ''}`,
                    labelRightIcon: !!option.identification && (
                      <StyledIdentification>
                        <CustomText fontSize={13} fontWeight={600} fontColor="#888">
                          {option.identification}
                        </CustomText>
                      </StyledIdentification>
                    ),
                    itemIcon: !!option.pms && <img src={icon_dentweb} alt="icon_dentweb" />,
                  },
                };
              })}
              bottomComponent={
                isNewPatient && (
                  <CustomText fontSize={12} fontColor="#00a6e2">
                    <T>PROJECT_NEW_PATIENT</T>
                  </CustomText>
                )
              }
              hasMoreData={hasMoreProjectPatients}
              loading={projectPatientsPending}
              onOptionClick={onOptionClick}
              onSearch={onPatientSearch}
              onFetch={onPatientFetch}
              placeholder={`${t('GLOBAL_PATIENT_NAME')} (${t('PROJECT_PATIENT_LIMIT_LENGTH')})`}
            />
            {!isEdit && !isPatient && (
              <Tooltip
                className="projectInformation__offline_tooltip"
                title={t('NOT_USE_OFFLINE')}
                placement="top"
                disableHoverListener={loadedSource !== 'local'}
                disableFocusListener={true}
                disableTouchListener={true}
              >
                <button
                  className="icon_box"
                  onClick={onCreatePatient}
                  disabled={loadedSource === 'local'}
                >
                  <img src={icon_add_patient} />
                </button>
              </Tooltip>
            )}
          </div>
        </div>
        <div className="createProjectInfo__item_box projectName">
          <div className="item_label">
            <CustomText fontSize={16} fontWeight={600} fontColor="#888">
              <T>PROJECT_NAME</T>
            </CustomText>
            <CustomText fontSize={16} fontWeight={600} fontColor="#D40000">
              *
            </CustomText>
          </div>
          <TextFieldController
            disabled={isEdit}
            name="projectName"
            control={control}
            required
            placeholder={t('PROJECT_NAME')}
            onChange={onProjectNameChange}
          />
        </div>
        <div className="createProjectInfo__item_box dueDate">
          <div className="item_label">
            <CustomText fontSize={16} fontWeight={600} fontColor="#888">
              <T>PROJECT_DUE_DATE</T>
            </CustomText>
          </div>
          <div className="item_content">
            <DatePickerController
              name="dueDate"
              control={control}
              open={isOpenDueDate}
              onOpenChange={onOpenChangeDueDate}
            />
            <button className="icon_box" onClick={onClickDueDate}>
              <img src={icon_project_calendar} />
            </button>
          </div>
        </div>
        <div className="createProjectInfo__item_box manager">
          <div className="item_label">
            <CustomText fontSize={16} fontWeight={600} fontColor="#888">
              <T>PROJECT_MANAGER</T>
            </CustomText>
          </div>
          <DropdownController
            name="manager"
            control={control}
            options={managerOptionList}
            placeholder={t('GLOBAL_MANAGER')}
          />
        </div>
      </div>
      {/* button box */}
      <div className="createProjectInfo__button_box">
        <MuiButton
          disableElevation
          disabled={!isValidInfo}
          variant="contained"
          className="createProjectInfo__button_item"
          onClick={() => {
            if (from === 'create' || from === 'patient') {
              onCreateInfoSave();
            }
            if (from === 'detail') {
              onDetailInfoSave();
            }
          }}
        >
          <T>GLOBAL_SAVE</T>
        </MuiButton>
      </div>

      {useMemo(() => {
        return (
          <PlainModal isOpen={isOpenPatientModal} onClose={onClosePatientModal} width={1000}>
            <AppModal
              title={'Patient'}
              content={
                <PatientModalContainer
                  type="new"
                  onCloseModal={onClosePatientModal}
                  name={patientName || ''}
                />
              }
              contentCardStyle={{
                padding: '0px',
                backgroundColor: 'transparent',
              }}
              isCloseIcon={true}
              onClick={onClosePatientModal}
              onCancel={onClosePatientModal}
              hideButton={true}
            />
          </PlainModal>
        );
      }, [isOpenPatientModal, patientName])}
      {useMemo(() => {
        return (
          <ClinicModal
            title={t('GLOBAL_ANNOUNCEMENT')}
            isOpen={isOpenMicrophoneModal}
            onClose={onCloseMicrophoneModal}
            onCancel={onCloseMicrophoneModal}
            onConfirm={onConfirmMicrophoneModal}
          >
            <MicrophoneAlertModal />
          </ClinicModal>
        );
      }, [isOpenMicrophoneModal])}
    </StyledCreateProjectInfo>
  );
};

const StyledCreateProjectInfo = styled.div`
  width: 100%;
  /* height: 658px; */
  background: #fff;
  padding: 30px;
  border-radius: 10px;
  .createProjectInfo__title_box {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 30px;
  }
  .createProjectInfo__content_box {
    display: flex;
    flex-direction: column;
    row-gap: 20px;
    column-gap: 20px;
    margin-bottom: 40px;
    .createProjectInfo__item_box {
      display: flex;
      flex-direction: column;
      row-gap: 5px;
      .item_label {
        display: flex;
        column-gap: 3px;
      }
      .item_content {
        display: flex;
        column-gap: 10px;
        .icon_box {
          width: 50px;
          height: 50px;
          border-radius: 2px;
          border: 1px solid #e1e1e7;
        }
      }
      &.senderMemo {
        position: relative;
        .memo_text_field {
          .textFieldController__input {
            &.textarea {
              padding-bottom: 60px;
            }
          }
        }
        .record_box {
          position: absolute;
          bottom: 12px;
          right: 12px;
          background: #444;
          display: flex;
          justify-content: center;
          align-items: center;
          width: 50px;
          height: 50px;
          border-radius: 50px;
          &.recording {
            width: 174px;
            background-color: #d85066;
            border-radius: 50px;
            height: 50px;
            column-gap: 20px;
          }
        }
      }
      &:nth-child(5) {
        grid-column: span 2;
      }
      &:nth-child(6) {
        grid-column: span 2;
      }
    }
  }
  .createProjectInfo__button_box {
    display: flex;
    justify-content: flex-end;
    .createProjectInfo__button_item {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 190px;
      height: 45px;
      padding: 0 15px;
      border-radius: 3px;
      color: white;
    }
  }
`;

const StyledIdentification = styled.div`
  width: 18px;
  height: 18px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 3px;
  border: 1px solid #e1e1e7;
  background-color: #f9f9fb;
`;

export default CreateProjectInfo;
